import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';
import { PHONE_CODES } from '../../lib/country-data';
import { localizedCountryName } from '../../lib/country-names';
import {
  STUDIO_COUNTRIES,
  filterStudioCountries,
  type StudioCountryCode,
} from '../../lib/application-studio-tool-config';

type StudioCountryOption = (typeof STUDIO_COUNTRIES)[number];

type Props = {
  value: StudioCountryCode;
  onChange: (value: StudioCountryCode) => void;
  title: string;
  searchPlaceholder: string;
};

function countryMeta(option: StudioCountryOption) {
  return PHONE_CODES.find((country) => country.code === option.isoCode);
}

function optionLabel(option: StudioCountryOption) {
  return countryMeta(option)?.name ?? option.label;
}

export function StudioCountryPicker({
  value,
  onChange,
  title,
  searchPlaceholder,
}: Props) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = STUDIO_COUNTRIES.find((item) => item.value === value) ?? STUDIO_COUNTRIES[0];
  const selectedMeta = countryMeta(selected);

  // Localized display name: Arabic full country name when locale === 'ar',
  // otherwise the existing English label (GCC short overrides preserved).
  const displayName = (option: StudioCountryOption) =>
    localizedCountryName(option.isoCode, locale, optionLabel(option));

  const filtered = useMemo(() => filterStudioCountries(search), [search]);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const select = (item: StudioCountryOption) => {
    onChange(item.value);
    close();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          minHeight: 54,
          borderRadius: 999,
          border: '1px solid var(--tabbio-border)',
          background: 'var(--tabbio-bg)',
          color: 'var(--tabbio-text)',
          padding: '8px 16px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'start',
        }}
      >
        <span style={{ fontSize: 20, lineHeight: 1 }}>{selectedMeta?.flag}</span>
        <span style={{ flexShrink: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {displayName(selected)}
          </span>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tabbio-text-tertiary)' }}>
            {selected.label}
          </span>
        </span>
        <span style={{ flex: 1 }} aria-hidden />
        <ChevronDown size={16} color="var(--tabbio-text-tertiary)" strokeWidth={2} />
      </button>

      {open ? (
        <div
          role="presentation"
          onClick={(event) => {
            if (event.currentTarget === event.target) close();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.36)',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              maxHeight: 'min(560px, calc(100vh - 48px))',
              minHeight: 360,
              borderRadius: 20,
              border: '1px solid var(--tabbio-border-subtle)',
              background: 'var(--tabbio-bg)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '14px 20px',
                borderBottom: '1px solid var(--tabbio-border-subtle)',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--tabbio-text)' }}>
                {title}
              </span>
              <button
                type="button"
                onClick={close}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="var(--tabbio-text-secondary)" strokeWidth={2} />
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 44,
                margin: '10px 16px',
                padding: '0 12px',
                borderRadius: 999,
                border: '1px solid var(--tabbio-border)',
                background: 'var(--tabbio-bg)',
              }}
            >
              <Search size={14} color="var(--tabbio-text-tertiary)" strokeWidth={2} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--tabbio-text)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filtered.map((item) => {
                const meta = countryMeta(item);
                const active = item.value === value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => select(item)}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = 'var(--tabbio-surface)';
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      padding: '13px 20px',
                      border: 'none',
                      borderBottom: '1px solid var(--tabbio-border-subtle)',
                      background: 'transparent',
                      color: 'var(--tabbio-text)',
                      cursor: 'pointer',
                      textAlign: 'start',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ width: 28, textAlign: 'center', fontSize: 21 }}>{meta?.flag}</span>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: active ? 700 : 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayName(item)}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tabbio-text-tertiary)' }}>
                        {item.label}
                      </span>
                    </span>
                    {active ? <Check size={18} color="var(--color-primary, #5e29ff)" strokeWidth={2.5} /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
